import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type RefObject } from "react";
import { AnimatePresence, animate, motion, useMotionValue, useReducedMotion, useTransform } from "../../../lib/motion/index.js";
import { pastel } from "../../task-colors.js";

/** A control selection to animate. `originRect` is the tapped control's screen
 *  rect (the blob launch / reveal start point). */
export interface WashPick {
  id: number;
  kind: "topic" | "color" | "priority";
  color: string;
  originRect: DOMRect;
}

interface Props {
  panelRef: RefObject<HTMLDivElement | null>;
  /** The topic blob's flight target (the title). */
  titleRef: RefObject<HTMLElement | null>;
  /** The element whose bottom edge splits the top band from the body band
   *  (the whole top color area — title + create + priority + slider). */
  boundaryRef: RefObject<HTMLElement | null>;
  /** Currently-selected topic colors (top band: solid / horizontal gradient). */
  topicColors: string[];
  /** Custom color (fills the body band). */
  bodyColor: string | null;
  /** Priority color (the sheet border). */
  priorityColor: string | null;
  pick: WashPick | null;
}

/** The settled background state of the wash; the bands lag behind `target` so a
 *  reveal grows the NEW visual over the OLD one. */
interface Visual {
  topColors: string[];
  bodyColor: string | null;
  border: string | null;
}

interface BlobState { id: number; from: { x: number; y: number }; to: { x: number; y: number }; color: string; onDone: () => void }
interface RevealState { id: number; ox: number; oy: number; r: number; visual: Visual }
interface SweepState { id: number; color: string; w: number; h: number; splitX: number }

/** Pastel softening of the top (topic) band — exported so the sheet can compute
 *  a readable ink against the same effective color. */
export const TOP_SOFT = 0.5;
const BODY_SOFT = 0.58;

function topBandBg(colors: string[]): string | undefined {
  if (colors.length === 0) return undefined;
  if (colors.length === 1) return colors[0];
  return `linear-gradient(90deg, ${colors.join(", ")})`;
}
/** Body fill. With a custom color it blends from the top-section color into the
 *  custom color over a short seam ("gradient between the top section and the
 *  custom color"). With NO custom color it fades the top-section color(s) down
 *  to white/transparent — the SAME horizontal topic gradient as the top band
 *  (so several topics all appear), masked to fade out going down. */
function bodyBandStyle(v: Visual): CSSProperties {
  if (v.bodyColor) {
    const top = topBandBg(v.topColors);
    if (!top) return { background: v.bodyColor };
    if (v.topColors.length === 1) {
      return { background: `linear-gradient(to bottom, ${v.topColors[0]}, ${v.bodyColor} 72px)` };
    }
    return {
      backgroundImage: `linear-gradient(to bottom, transparent 0, ${v.bodyColor} 72px), ${top}`,
    };
  }
  if (v.topColors.length === 0) return {};
  const fade = "linear-gradient(to bottom, black, transparent)";
  return { background: topBandBg(v.topColors), maskImage: fade, WebkitMaskImage: fade };
}
function sameVisual(a: Visual, b: Visual): boolean {
  return a.bodyColor === b.bodyColor && a.border === b.border
    && a.topColors.length === b.topColors.length && a.topColors.every((c, i) => c === b.topColors[i]);
}
function maxRadius(ox: number, oy: number, w: number, h: number): number {
  return Math.max(Math.hypot(ox, oy), Math.hypot(w - ox, oy), Math.hypot(ox, h - oy), Math.hypot(w - ox, h - oy));
}

/**
 * The sheet's color wash. Two persistent bands sit BEHIND the form content: the
 * top band (window top → title's bottom edge) carries the topic color(s); the
 * body band (title → window bottom) carries the custom color, blending from the
 * top color at the seam. The border is the priority color.
 *
 * Each selection animates as ONE motion: a tinted blob flies from the tapped
 * control to the title (topic) / window top (priority), and on impact the entire
 * NEW visual (top fill + body gradient together) floods in via a single circle
 * clipped from the impact point — so the fill and its gradient grow as one, and
 * multiple topics reveal their combined gradient. Custom color floods straight
 * from its swatch (no blob); priority traces a border sweep that propagates both
 * ways from the impact and matches the panel's actual border shape. Disabled
 * under reduced motion (the color applies instantly).
 */
export function SheetWash({ panelRef, titleRef, boundaryRef, topicColors, bodyColor, priorityColor, pick }: Props) {
  const reduce = useReducedMotion();

  const target: Visual = {
    topColors: topicColors.filter(Boolean).map((c) => pastel(c, TOP_SOFT)),
    bodyColor: bodyColor ? pastel(bodyColor, BODY_SOFT) : null,
    border: priorityColor,
  };
  const targetRef = useRef(target);
  targetRef.current = target;
  const targetKey = `${target.topColors.join("|")}~${target.bodyColor}~${target.border}`;

  const [committed, setCommitted] = useState<Visual>({ topColors: [], bodyColor: null, border: null });
  const [topH, setTopH] = useState(0);
  const [blob, setBlob] = useState<BlobState | null>(null);
  const [reveal, setReveal] = useState<RevealState | null>(null);
  const [sweep, setSweep] = useState<SweepState | null>(null);
  // Ignore a stale pick that's still set when the sheet (re)opens — otherwise a
  // previous session's selection fires a phantom bloom on open.
  const handledPick = useRef(pick?.id ?? 0);
  const seq = useRef(0);
  // `onAnimationComplete` fires for BOTH the travel AND the exit of a blob, so
  // guard `onDone` to run exactly once per blob (else the reveal fires twice).
  const firedBlob = useRef(0);
  const busy = !!blob || !!reveal;
  const busyRef = useRef(busy);
  busyRef.current = busy;

  // Boundary between the two bands = the title's bottom edge (it shifts when the
  // title morphs, so track it with a RO). The parent sets the sheet height in a
  // LATER layout effect, so the first measure can run against a 0-height panel —
  // rAF retries settle it once the panel is laid out.
  useLayoutEffect(() => {
    const measure = () => {
      const panel = panelRef.current?.getBoundingClientRect();
      const boundary = boundaryRef.current?.getBoundingClientRect();
      if (panel && boundary) {
        const next = Math.max(0, Math.round(boundary.bottom - panel.top));
        setTopH((prev) => (prev === next ? prev : next));
      }
    };
    measure();
    const r1 = requestAnimationFrame(measure);
    const r2 = requestAnimationFrame(() => requestAnimationFrame(measure));
    const ro = new ResizeObserver(measure);
    if (panelRef.current) ro.observe(panelRef.current);
    if (boundaryRef.current) ro.observe(boundaryRef.current);
    return () => { cancelAnimationFrame(r1); cancelAnimationFrame(r2); ro.disconnect(); };
  }, [panelRef, boundaryRef]);

  // Flood the whole NEW visual in from (ox, oy) — panel-relative px.
  const fireReveal = (ox: number, oy: number) => {
    const panel = panelRef.current?.getBoundingClientRect();
    if (!panel) { setCommitted(targetRef.current); return; }
    seq.current += 1;
    setReveal({ id: seq.current, ox, oy, r: maxRadius(ox, oy, panel.width, panel.height), visual: targetRef.current });
  };

  useEffect(() => {
    const panel = panelRef.current?.getBoundingClientRect();
    const isNewPick = pick && pick.id !== handledPick.current;

    if (isNewPick) {
      handledPick.current = pick!.id;
      if (reduce || !panel) { setCommitted(targetRef.current); return; }
      const o = pick!.originRect;
      const cx = o.left + o.width / 2 - panel.left;
      const cy = o.top + o.height / 2 - panel.top;

      if (pick!.kind === "color") {
        fireReveal(cx, cy); // body floods straight from the swatch
        return;
      }
      if (pick!.kind === "topic") {
        const title = titleRef.current?.getBoundingClientRect();
        const toY = title ? title.top + title.height / 2 - panel.top : Math.max(8, topH - 8);
        seq.current += 1;
        setBlob({ id: seq.current, color: pick!.color, from: { x: cx, y: cy }, to: { x: cx, y: toY }, onDone: () => fireReveal(cx, toY) });
        return;
      }
      // priority: blob straight up to the top edge, then a two-way border sweep.
      seq.current += 1;
      setBlob({
        id: seq.current, color: pick!.color, from: { x: cx, y: cy }, to: { x: cx, y: 3 },
        onDone: () => {
          const p = panelRef.current?.getBoundingClientRect();
          if (p) { seq.current += 1; setSweep({ id: seq.current, color: pick!.color, w: p.width, h: p.height, splitX: cx }); }
          setCommitted((c) => ({ ...c, border: targetRef.current.border }));
        },
      });
      return;
    }

    // Non-pick change (deselect / color removal / kind toggle): settle instantly.
    // `busy` is a dep so a change made DURING an animation still commits once it
    // finishes (busy → false re-runs this).
    if (busyRef.current) return;
    setCommitted((c) => (sameVisual(c, targetRef.current) ? c : targetRef.current));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pick?.id, targetKey, topH, busy]);

  return (
    <>
      <div className="task-sheet-wash" aria-hidden>
        <Bands visual={committed} topH={topH} />
        {reveal && (
          <RevealLayer
            key={reveal.id}
            reveal={reveal}
            topH={topH}
            onDone={() => { setCommitted(targetRef.current); setReveal(null); }}
          />
        )}
      </div>

      <div className="task-sheet-fx" aria-hidden>
        <AnimatePresence>
          {blob && (
            <motion.span
              key={`blob-${blob.id}`}
              className="absolute h-5 w-5 rounded-full"
              style={{ background: blob.color, boxShadow: `0 0 14px ${blob.color}` }}
              initial={{ x: blob.from.x - 10, y: blob.from.y - 10, scale: 0.5, opacity: 0.95 }}
              animate={{ x: blob.to.x - 10, y: blob.to.y - 10, scale: 1.25, opacity: 1 }}
              exit={{ opacity: 0, scale: 1.5, transition: { duration: 0.14 } }}
              transition={{ duration: 0.26, ease: "easeOut" }}
              onAnimationComplete={() => {
                if (firedBlob.current === blob.id) return;
                firedBlob.current = blob.id;
                const done = blob.onDone;
                setBlob(null);
                done();
              }}
            />
          )}
        </AnimatePresence>
        <AnimatePresence>
          {sweep && <BorderSweep key={`sweep-${sweep.id}`} sweep={sweep} onDone={() => setSweep(null)} />}
        </AnimatePresence>
      </div>
    </>
  );
}

/** The two persistent (or revealed) background bands. */
function Bands({ visual, topH }: { visual: Visual; topH: number }) {
  return (
    <>
      <div className="tsw-band" style={{ top: 0, height: topH, background: topBandBg(visual.topColors) }} />
      <div className="tsw-band" style={{ top: topH, bottom: 0, ...bodyBandStyle(visual) }} />
    </>
  );
}

/** The new visual painted full-panel and revealed by a circle clipped from the
 *  impact point — top fill + body gradient grow together as a single motion. */
function RevealLayer({ reveal, topH, onDone }: { reveal: RevealState; topH: number; onDone: () => void }) {
  const radius = useMotionValue(0);
  const clip = useTransform(radius, (v) => `circle(${v}px at ${reveal.ox}px ${reveal.oy}px)`);
  useEffect(() => {
    const controls = animate(radius, reveal.r, { duration: 0.5, ease: [0.22, 0.61, 0.36, 1], onComplete: onDone });
    return () => controls.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <motion.div className="absolute inset-0 overflow-hidden rounded-[inherit]" style={{ clipPath: clip, WebkitClipPath: clip }}>
      <Bands visual={reveal.visual} topH={topH} />
    </motion.div>
  );
}

/** Two stroked paths tracing the panel's actual border (rounded top, square
 *  bottom) from the impact x in both directions, meeting at the bottom-center. */
function BorderSweep({ sweep, onDone }: { sweep: SweepState; onDone: () => void }) {
  const { w, h, color, splitX } = sweep;
  const inset = 1;
  const rr = 15; // matches rounded-t-2xl (16) minus the 1px inset
  const sx = Math.max(inset + rr, Math.min(w - inset - rr, splitX));
  const right = `M ${sx} ${inset} H ${w - inset - rr} A ${rr} ${rr} 0 0 1 ${w - inset} ${inset + rr} V ${h - inset} H ${w / 2}`;
  const left = `M ${sx} ${inset} H ${inset + rr} A ${rr} ${rr} 0 0 0 ${inset} ${inset + rr} V ${h - inset} H ${w / 2}`;
  const stroke = { stroke: color, strokeWidth: 2, fill: "none" as const };
  return (
    <motion.svg className="absolute inset-0" width={w} height={h} fill="none" initial={{ opacity: 1 }} exit={{ opacity: 0, transition: { duration: 0.3 } }}>
      <motion.path d={right} {...stroke} initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 0.45, ease: "easeOut" }} onAnimationComplete={onDone} />
      <motion.path d={left} {...stroke} initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 0.45, ease: "easeOut" }} />
    </motion.svg>
  );
}
